// react
import React from 'react';
import {
  Animated,
  AppState,
  AppStateStatus,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';

// @polls-platform/core
import {
  ANIMATION_DURATION,
  REFRESH_POLL_AGE_SECONDS,
  WEBVIEW_ORIGIN_WHITELIST,
  getConfig,
  getThemeProperties,
  getThemeFromPollUrl,
  setPollUrlParams,
  EmbeddedMessage,
  EmbeddedMessageEvent,
  ThemeProperties,
  OpenPollFlow,
  OptionResponse,
  Theme,
  AnalyticsEvent,
  constructPollUrl,
  isPollSourcePollId,
} from '@polls-platform/core/built/internal';

// @polls-platform/react-native
import { LoadingView, getEmbeddedState } from '../../internal';

// third party dependencies
import { WebView } from 'react-native-webview';
import type { WebViewMessageEvent } from 'react-native-webview/lib/WebViewTypes';

/**
 * Props to pass the `PollView` Component
 * @category 3. PollView
 */
interface PollViewProps {
  /**
   * styles to be applied to the outer most component of the PollView.
   * styles will be merged by overriding default styles
   */
  style?: ViewStyle;
  /**
   * The poll source can be either a Poll ID or Poll URL
   * When creating a poll with the `createPoll` or `createPollAsync` functions
   * the url returned by those functions can be passed directly to this prop.
   * This prop is also used when opening an existing poll.
   */
  pollSource: string;
  /**
   * The `PollView` component is used for both creating polls and opening polls.
   * This property is used to determine which flow the user is in
   */
  openPollFlow: OpenPollFlow;
  /**
   * Controls the appearance of the loading screen when creating/opening a poll
   * default: light
   */
  theme?: Theme;
  /**
   * This callback will fire when the user wants to view the details of one of the options.
   * When this fires, the user should be navigated to a new screen or a modal/popover should be presented.
   * Note that the user should be able to easily navigate back to the poll to complete their vote.
   */
  didPressViewDetails?: (option: OptionResponse) => void;
  /**
   * This callback forwards analytics events that occur within the poll.
   * For more information, go to https://docs.pollsplatform.com/analytics
   * Analytics can be forwarded to your own data warehouse such as Google Analytics.
   */
  didReceiveAnalyticsEvent?: (event: AnalyticsEvent) => void;
}

/**
 * The `PollView` component is used for both creating and opening polls.
 * For more information, go to https://docs.pollsplatform.com/react-native.
 * Before navigating the user to this screen, you must have a poll URL.
 * You can create a poll using the `createPoll` and `createPollAsync` functions
 * which will respond with a URL that can be passed here.
 * Or if you already have the URL of an existing poll you can pass it
 * @category 3. PollView
 */
const PollView = ({
  style,
  pollSource,
  openPollFlow,
  theme = Theme.light,
  didPressViewDetails,
  didReceiveAnalyticsEvent,
}: PollViewProps) => {
  // state
  const [isLoading, setIsLoading] = React.useState(true);
  const [showWebView, setShowWebView] = React.useState(false);
  const [loadingTitleOverride, setLoadingTitleOverride] = React.useState<
    string | null
  >(null);
  const lastLoadTime = React.useRef<number | null>(null);
  const webViewRef = React.useRef<WebView>(null);

  const themeProperties = getThemeProperties(theme);

  // poll url
  const pollUrl = React.useMemo(() => {
    if (isPollSourcePollId(pollSource)) {
      // found poll id, construct url
      const pollId = pollSource;
      return constructPollUrl({
        pollId: pollId,
        flow: openPollFlow,
        // pollState?: PollState;
        // themeName?: string;
        // themeObject?: any;
      });
    } else {
      // found poll url
      return pollSource; // Poll Source = Poll URL
    }
  }, [pollSource, openPollFlow]);

  // handle poll url changing
  React.useEffect(() => {
    lastLoadTime.current = Date.now();
    setIsLoading(true);
  }, [pollUrl]);

  // manage loading states
  const loadingOpacity = React.useRef(new Animated.Value(0)).current; // initial
  const webViewOpacity = React.useRef(new Animated.Value(0)).current; // initial

  // show/hide loading
  React.useEffect(() => {
    Animated.timing(loadingOpacity, {
      toValue: isLoading ? 1 : 0,
      duration: ANIMATION_DURATION,
      useNativeDriver: true,
    }).start();

    if (isLoading) {
      setShowWebView(false);
    } else {
      setTimeout(() => {
        setShowWebView(true);
      }, ANIMATION_DURATION);
    }
    // we do not want to run this if the animation ref changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // show/hide web view
  React.useEffect(() => {
    Animated.timing(webViewOpacity, {
      toValue: showWebView ? 1 : 0,
      duration: ANIMATION_DURATION,
      useNativeDriver: true,
    }).start();

    if (showWebView) {
      setTimeout(() => {
        setLoadingTitleOverride(null);
      }, ANIMATION_DURATION);
    }
    // we do not want to run this if the animation ref changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWebView]);

  // reload when foregrounding after 3m
  React.useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState) => {
      const activeState: AppStateStatus = 'active';
      const secondsSinceLastLoad = Math.round(
        (Date.now() - (lastLoadTime.current || 0)) / 1000
      );
      if (
        nextAppState === activeState &&
        secondsSinceLastLoad > REFRESH_POLL_AGE_SECONDS
      ) {
        setLoadingTitleOverride('Reloading Poll...'); // TODO: translation
        setShowWebView(false);
        setIsLoading(true);
        lastLoadTime.current = Date.now();
        webViewRef.current?.reload();
      }
    });

    return () => {
      subscription.remove();
    };
  }, []);

  // external callbacks
  const handleDidPressViewDetails = (option: OptionResponse) => {
    didPressViewDetails?.(option);
  };

  const handleDidReceiveAnalyticsEvent = (event: AnalyticsEvent) => {
    didReceiveAnalyticsEvent?.(event);
  };

  // internal callbacks
  const handleOnLoadEnd = () => {
    setTimeout(() => {
      setIsLoading(false);
    }, 250);
  };

  const handleOnMessage = (event: WebViewMessageEvent) => {
    const trackInvalidMessage = () => {
      // console.log('❌ invalid message', event.nativeEvent.data);
    };

    // guards
    const dataString = event.nativeEvent.data;
    if (!dataString) {
      trackInvalidMessage();
      return;
    }
    const data: unknown = JSON.parse(dataString);
    if (!data) {
      trackInvalidMessage();
      return;
    }

    const parseResult = EmbeddedMessage.safeParse(data);

    if (!parseResult.success) {
      trackInvalidMessage();
      return;
    }

    switch (parseResult.data.event) {
      case EmbeddedMessageEvent.VIEW_OPTION_DETAILS: {
        handleDidPressViewDetails(parseResult.data.payload.option);
        break;
      }
      case EmbeddedMessageEvent.ANALYTICS_EVENT: {
        handleDidReceiveAnalyticsEvent(parseResult.data.payload);
        break;
      }
      default: {
        break;
      }
    }
  };

  // render

  const embeddedPollUrl = setPollUrlParams(pollUrl, {
    embeddedState: getEmbeddedState(),
  });

  const subdomain =
    getThemeFromPollUrl(embeddedPollUrl) ?? getConfig().domainConfig.subdomain;
  const loadingLogoImageUrl = `https://polls-platform.s3.amazonaws.com/customer-assets/${subdomain}/mobileLoadingLogo.png`;

  let loadingTitle: string;

  if (loadingTitleOverride) {
    loadingTitle = loadingTitleOverride;
  } else {
    switch (openPollFlow) {
      case OpenPollFlow.createPoll: {
        loadingTitle = 'Creating Poll...'; // TODO: translation
        break;
      }
      default:
      case OpenPollFlow.openPoll: {
        loadingTitle = 'Loading Poll...'; // TODO: translation
        break;
      }
    }
  }

  const styles = createStyles({ themeProperties });

  return (
    <View style={[styles.container, style]}>
      <View style={styles.content}>
        <Animated.View
          style={{
            ...styles.webViewContainer,
            opacity: webViewOpacity,
          }}
        >
          <WebView
            style={styles.webView}
            source={{ uri: embeddedPollUrl }}
            ref={webViewRef}
            onLoadEnd={handleOnLoadEnd}
            originWhitelist={WEBVIEW_ORIGIN_WHITELIST}
            onMessage={handleOnMessage}
          />
        </Animated.View>
        {!showWebView && (
          <Animated.View
            style={{
              ...styles.loadingContainer,
              opacity: loadingOpacity,
            }}
          >
            <LoadingView
              isVisible={isLoading}
              title={loadingTitle}
              imageUrl={loadingLogoImageUrl}
              theme={theme}
            />
          </Animated.View>
        )}
      </View>
    </View>
  );
};

const createStyles = (styleProps: { themeProperties: ThemeProperties }) => {
  const { themeProperties } = styleProps;
  const { colors: themeColors } = themeProperties;
  const styles = StyleSheet.create({
    container: {
      width: '100%',
      height: '100%',
      backgroundColor: themeColors.backgroundColor,
    },
    content: {
      width: '100%',
      height: '100%',
      backgroundColor: themeColors.backgroundColor,
    },
    loadingContainer: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      backgroundColor: themeColors.backgroundColor,
    },
    webViewContainer: {
      width: '100%',
      height: '100%',
    },
    webView: {
      width: '100%',
      height: '100%',
    },
  });

  return styles;
};

export { PollView };
